import { useEffect, useRef, useState } from 'react';
import { ChevronDown, Download, FileSpreadsheet, FileText, Loader2 } from 'lucide-react';
import { exportarCSV } from './csv';
import { exportarXLSX } from './xlsx';
import { useAvisos } from '../lib/contextoAvisos';
import { TOPE_EXPORTACION } from '../api';

// Exporta la lista a un archivo. En TODAS las listas, sin excepción: es
// lo que tranquiliza a un cliente que desconfía de meter sus datos a un
// sistema nuevo.
//
// DOS FALLOS QUE ESTO ARREGLA, de la misma familia —el archivo que se
// entrega no es lo que dice ser—:
//
// 1. Exportaba `filas`, es decir lo que hubiera en pantalla. Con la lista
//    paginada de 100 en 100, quien tenía 340 productos recibía un archivo
//    llamado `productos.csv` con 100 renglones y ninguna señal de que
//    faltaran 240. Ahora, cuando la pantalla sabe pedirle al servidor el
//    resto —eso es `obtenerTodo`—, se exporta TODO lo que cumple el
//    filtro; y si algo lo impide, se dice.
//
// 2. Solo había CSV, y un CSV no declara tipos: Excel los adivina al
//    abrirlo, en la máquina del contador. El código de barras
//    7501234567890 llegaba como 7.50123E+12 y el SKU 0012345 sin sus
//    ceros. Por eso Excel es ahora la opción por omisión y el CSV queda
//    para lo que de verdad lo necesita: importar a otro sistema.
export default function BotonExportar({ nombre, columnas, filas, obtenerTodo, pie }) {
  const [ocupado, setOcupado] = useState(false);
  const [avance, setAvance] = useState(0);
  const [menu, setMenu] = useState(false);
  const contenedor = useRef(null);
  const { exito, aviso, error } = useAvisos();

  const vacio = !filas || filas.length === 0;

  // Un menú que solo se cierra con su propio botón deja al usuario
  // atrapado: se cierra al hacer clic fuera y con Escape, como cualquier
  // otro menú del sistema.
  useEffect(() => {
    if (!menu) return undefined;
    const fuera = (e) => {
      if (!contenedor.current?.contains(e.target)) setMenu(false);
    };
    const escape = (e) => { if (e.key === 'Escape') setMenu(false); };
    document.addEventListener('mousedown', fuera);
    document.addEventListener('keydown', escape);
    return () => {
      document.removeEventListener('mousedown', fuera);
      document.removeEventListener('keydown', escape);
    };
  }, [menu]);

  async function exportar(formato) {
    setMenu(false);
    const escribir = formato === 'csv'
      ? (f) => exportarCSV(nombre, columnas, f, pie)
      : (f) => exportarXLSX(nombre, columnas, f, pie);
    const extension = formato === 'csv' ? 'csv' : 'xlsx';

    if (!obtenerTodo) {
      // Listas que ya vienen completas del servidor (árboles, catálogos
      // cortos). Aquí `filas` SÍ es todo.
      setOcupado(true);
      try {
        await escribir(filas || []);
      } catch (e) {
        error(`No se pudo generar el archivo: ${e.message}`);
      } finally {
        setOcupado(false);
      }
      return;
    }

    setOcupado(true);
    setAvance(0);
    try {
      const { filas: todas, completo } = await obtenerTodo(setAvance);
      await escribir(todas);
      if (completo) {
        exito(`Exportados ${todas.length} registros a ${nombre}.${extension}`);
      } else {
        // Un recorte anunciado es aceptable; uno silencioso no.
        aviso(
          `El archivo trae los primeros ${TOPE_EXPORTACION.toLocaleString('es-MX')} `
          + 'registros, que es el máximo que el navegador aguanta de una vez. '
          + 'Afina los filtros o exporta por rangos de fecha para llevártelo completo.',
        );
      }
    } catch (e) {
      error(`No se pudo completar la exportación: ${e.message}`);
    } finally {
      setOcupado(false);
    }
  }

  const claseOpcion = 'w-full flex items-start gap-2.5 px-3 py-2 text-left text-sm '
    + 'hover:bg-gray-50 focus:outline-none focus:bg-gray-50';

  return (
    <div className="relative" ref={contenedor}>
      <button
        type="button"
        onClick={() => setMenu((m) => !m)}
        disabled={vacio || ocupado}
        title={vacio ? 'La lista está vacía: cuando tenga renglones se podrán exportar.' : undefined}
        aria-busy={ocupado || undefined}
        aria-haspopup="menu"
        aria-expanded={menu}
        className="inline-flex items-center gap-1.5 border border-gray-300 bg-white text-gray-700
                   text-sm font-semibold px-3 py-2 rounded-lg hover:bg-gray-50
                   disabled:opacity-50 disabled:cursor-not-allowed
                   focus:outline-none focus:ring-2 focus:ring-[var(--color-marca)] focus:ring-offset-1"
      >
        {ocupado ? (
          <>
            <Loader2 className="h-4 w-4 animate-spin" />
            {/* El número tranquiliza: sin él, una exportación de 8 000
                renglones parece un botón que no hizo nada. */}
            {avance > 0 ? `Preparando… ${avance}` : 'Preparando…'}
          </>
        ) : (
          <>
            <Download className="h-4 w-4" /> Exportar
            {/* SIN RENGLONES NO HAY ARCHIVO, y el botón lo dice él mismo en
                vez de quedarse gris sin explicación (Doctrina 2, regla 8;
                auditoría del 19 sep 2026: ocho pantallas con este botón
                apagado y mudo). */}
            {vacio ? <span className="font-normal text-gray-500">· nada que exportar todavía</span>
              : <ChevronDown className="h-3.5 w-3.5 text-gray-500" />}
          </>
        )}
      </button>

      {menu && !ocupado && (
        <div
          role="menu"
          className="absolute right-0 z-20 mt-1 w-72 bg-white border border-gray-200
                     rounded-lg shadow-lg overflow-hidden"
        >
          <button type="button" role="menuitem" className={claseOpcion}
                  onClick={() => exportar('xlsx')}>
            <FileSpreadsheet className="h-4 w-4 mt-0.5 text-emerald-700 shrink-0" />
            <span>
              <span className="font-semibold text-gray-900 block">Excel (.xlsx)</span>
              <span className="text-xs text-gray-500">
                Cantidades e importes listos para sumar, y los códigos sin deformar.
              </span>
            </span>
          </button>
          <button type="button" role="menuitem"
                  className={`${claseOpcion} border-t border-gray-100`}
                  onClick={() => exportar('csv')}>
            <FileText className="h-4 w-4 mt-0.5 text-gray-500 shrink-0" />
            <span>
              <span className="font-semibold text-gray-900 block">CSV (texto)</span>
              <span className="text-xs text-gray-500">
                Para importar a otro sistema. Excel deforma códigos largos al abrirlo.
              </span>
            </span>
          </button>
        </div>
      )}
    </div>
  );
}
